import type { StrokePoints } from "../handwriting/stroke-shape.js";
import type { StrokeStepVerdict } from "../handwriting/stroke-step.js";
import { checkStroke } from "./xiezi-check.js";
import { type DrillProgress, inkStays } from "../handwriting/xiezi-stages.js";
import type { XieziSquare } from "./xiezi-square.js";

/**
 * The stroke that just landed on a drill square, read off the pad, marked and
 * taken off again. `xiezi-check.ts` is the marking, and this is the reading
 * around it that the first three stages do with every stroke.
 */

/**
 * Mark the stroke that just landed on a drill square, and take it off the pad.
 *
 * The first three stages read a stroke as it lands and clear it, the brush
 * standing in for it where it was taken. Nothing is marked where no stroke
 * has landed.
 *
 * Nothing is marked in the free stage either, where the ink stays on the pad
 * to be marked whole.
 *
 * @param square - The square, which holds the ink and its side.
 * @param taught - The character's strokes, as taught.
 * @param progress - Where the reader is, which says the stroke wanted.
 * @returns What the stroke was a go at, or nothing for an empty pad or a
 * stage that does not snap.
 */
export function checkLanded(
  square: Pick<XieziSquare, "ink" | "side" | "dropLastInk">,
  taught: readonly StrokePoints[],
  progress: DrillProgress,
): StrokeStepVerdict | undefined {
  const ink = square.ink.at(-1);
  const next = progress.stroke;

  if (ink === undefined || inkStays(progress)) {
    return undefined;
  }

  const verdict = checkStroke({ taught, next, ink, side: square.side });

  square.dropLastInk();

  return verdict;
}
